import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Trash2, Search, MapPin, Phone, Briefcase, Star, User, X, Upload, Edit, Save } from 'lucide-react';
import DataLookupField from '../components/DataLookupField';
import InteractiveTable from '../components/InteractiveTable';
import { AddressLookup, PostcodeLookup } from './Suppliers/components/AddressLookup';
import { SupplierCategoryAdder } from './Suppliers/components/SupplierCategoryAdder';
import { SupplierDocuments } from './Suppliers/components/SupplierDocuments';
import { useDecimalFormat } from '../contexts/ExpenditureLabelsContext';
import ModuleReports from './Reports/ModuleReports';
import { SUPPLIERS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, LayoutDashboard } from 'lucide-react';
import SuppliersDashboard from './SuppliersDashboard';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate, fmtDateTime } from '../utils/dateFormatting';





const EMPTY = {
  supplier_code: '', supplier_name: '', address: '', city: '', state_province: '',
  zip_postal: '', country: 'United States', website: '', duns_number: '',
  sic_code: '', sic_description: '',
  registration_number: '', co2e: 0, supplier_type: 'Approved', status: 'Approved',
  iso_rated: false, small_firm: false, sub_contractor: false, supplier_module: false,
  active: true, contacts: [], category_codes: [], documents: [],
  financial_details: { account_number: '', bank_name: '', tax_id: '', currency_code: 'USD', payment_terms: '', sort_swift_iban: '', sales_tax: 0, discount: 0 },
  punchout_details: { enabled: false, protocol: 'cXML', punchout_type: 'Test', buyer_identity: '', supplier_identity: '', shared_secret: '', catalog_url: '', order_url: '' },
  compliance: { diversity: {}, organization: {}, risk_info: {}, risk_status: {}, credit_score: {} },
  emissions: {},
};

const SUPPLIER_TYPES   = ['Strategic', 'Preferred', 'Approved', 'Under Review', 'Blocked'];
const SUPPLIER_STATUSES = ['Approved', 'Pending', 'Blocked', 'Inactive'];
const COUNTRIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'UAE', 'India', 'Singapore', 'Other'];

const EMPTY_CONTACT = { name: '', position: '', email: '', phone: '', is_main: false };

export default function SuppliersPage() {
  const { t } = useTranslation();
  const { fmtVal, fmtInput } = useDecimalFormat();
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [viewTab, setViewTab] = useState('list');
  const [records, setRecords] = useState([]);
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('supplier');
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sortState, setSortState] = useState({ field: 'supplier_code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [isEdit, setIsEdit]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [editingContactIdx, setEditingContactIdx] = useState(null);
  const [taxLooking, setTaxLooking] = useState(false);
  const [sicSearchTerm, setSicSearchTerm] = useState('');
  const [sicResults, setSicResults] = useState([]);
  const [sicDropOpen, setSicDropOpen] = useState(false);

  const searchSicCodes = async (q) => {
    setSicSearchTerm(q);
    if (q.length < 2) { setSicResults([]); return; }
    try {
      const r = await api.get(`/api/system/sic-codes?search=${encodeURIComponent(q)}`);
      setSicResults(r.data.data || []);
      setSicDropOpen(true);
    } catch { setSicResults([]); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/suppliers', {
        params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir }
      });
      setRecords(res.data.data || []); setTotal(res.data.total);
    } catch (err) {
      // Only show error for genuine server failures, not auth/network issues
      if (err?.response?.status && err.response.status >= 500) {
        toast.error('Failed to load suppliers');
      }
    }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const f  = (field, val) => setForm(p => ({ ...p, [field]: val }));
  const fd = (field, val) => setForm(p => ({ ...p, financial_details: { ...p.financial_details, [field]: val } }));
  const fp = (field, val) => setForm(p => ({ ...p, punchout_details: { ...p.punchout_details, [field]: val } }));
  const fc = (section, field, val) => setForm(p => ({ ...p, compliance: { ...p.compliance, [section]: { ...p.compliance[section], [field]: val } } }));

  const openCreate = () => { setForm({ ...EMPTY, contacts: [], category_codes: [], documents: [] }); setIsEdit(false); setSicSearchTerm(''); setModalOpen(true); };
  const openEdit   = async code => {
    try {
      const res = await api.get(`/api/data/suppliers/${code}`);
      const data = { ...EMPTY, ...res.data };
      if (data.financial_details) {
        data.financial_details.sales_tax = fmtInput(data.financial_details.sales_tax);
        data.financial_details.discount = fmtInput(data.financial_details.discount);
      }
      setForm(data); setIsEdit(true); setSicSearchTerm(data.sic_code ? `${data.sic_code} — ${data.sic_description || ''}` : ''); setModalOpen(true);
    }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load supplier'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/data/suppliers/${form.supplier_code}`, form);
      else        await api.post('/api/data/suppliers', form);
      toast.success(isEdit ? 'Updated' : 'Created');
      setModalOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
      setSaving(false);
      return;
    }
    setSaving(false);
    try { await load(); } catch {}
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this supplier?')) return;
    try { await api.delete(`/api/data/suppliers/${form.supplier_code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Delete failed'); }
  };

  // Contacts
  const setMainContact = (idx) => {
    setForm(p => ({
      ...p,
      contacts: p.contacts.map((c, i) => ({ ...c, is_main: i === idx }))
    }));
  };
  const addContact = () => {
    if (!newContact.name) return;
    const isFirst = (form.contacts || []).length === 0;
    setForm(p => ({ ...p, contacts: [...(p.contacts || []), { ...newContact, is_main: isFirst }] }));
    setNewContact(EMPTY_CONTACT);
  };
  const removeContact = i => setForm(p => ({
    ...p,
    contacts: p.contacts.filter((_, idx) => idx !== i).map((c, idx) => ({ ...c, is_main: idx === 0 && p.contacts[0]?.is_main ? true : c.is_main }))
  }));
  const updateContact = (idx, updated) => {
    setForm(p => ({ ...p, contacts: p.contacts.map((c, i) => i === idx ? { ...c, ...updated } : c) }));
    setEditingContactIdx(null);
  };


  const toggleFav = useCallback(async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('suppliers', r.supplier_code);
    if (result !== null) {
      // Optimistic local update — flip the star color immediately
      setRecords(prev => prev.map(rec => (rec.supplier_code === r.supplier_code) ? { ...rec, is_favorite: result } : rec));
      // Refetch so the backend re-sorts (favorites first) authoritatively
      load();
    }
  }, [load]);

  const supplierDefaultCols = useMemo(() => [
    { key: 'fav', label: '★', width: 44, visible: true, align: 'center', fixed: true },
    { key: 'supplier_code', label: 'Supplier Code', width: 140, sortField: 'supplier_code', visible: true },
    { key: 'supplier_name', label: 'Supplier Name', width: 200, sortField: 'supplier_name', visible: true },
    { key: 'primary_contact_name', label: 'Primary Contact', width: 160, visible: true },
    { key: 'primary_contact_phone', label: 'Phone', width: 130, visible: true },
    { key: 'active_contracts', label: 'Active Contracts', width: 120, visible: true, align: 'right' },
    { key: 'sic_description', label: 'SIC Description', width: 180, visible: true },
    { key: 'status', label: 'Status', width: 110, sortField: 'status', visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
    { key: 'small_firm', label: 'Small Firm', width: 100, sortField: 'small_firm', visible: false },
    { key: 'iso_rated', label: 'ISO Rated', width: 100, sortField: 'iso_rated', visible: false },
    { key: 'address', label: 'Address', width: 220, visible: false, addressCol: true },
    { key: 'city', label: 'City', width: 130, visible: true, addressCol: true },
    { key: 'state_province', label: 'State / Province', width: 140, visible: false, addressCol: true },
    { key: 'zip_postal', label: 'Zip / Postal', width: 110, visible: false, addressCol: true },
    { key: 'country', label: 'Country', width: 130, visible: false, addressCol: true },
  ], []);

  const renderSupplierCell = useCallback((col, r) => {
    switch (col.key) {
      case 'fav': return (
        <button
          onClick={(e) => { e.stopPropagation(); toggleFav(r); }}
          title={r.is_favorite ? 'Unfavorite' : 'Mark as favorite'}
          className={`transition-colors ${r.is_favorite ? 'text-amber-400' : 'text-zinc-300 hover:text-amber-400'}`}
          data-testid={`supplier-favorite-${r.supplier_code}`}
          aria-pressed={!!r.is_favorite}
        >
          <Star className={`h-4 w-4 ${r.is_favorite ? 'fill-amber-400' : ''}`} />
        </button>
      );
      case 'supplier_code': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.supplier_code}</span>;
      case 'supplier_name': return <span className="font-medium text-sm">{r.supplier_name}</span>;
      case 'status': return <Badge variant="outline" className="text-xs">{r.status}</Badge>;
      case 'active': return <ActiveBadge active={r.active} />;
      case 'small_firm': return <ActiveBadge active={r.small_firm} />;
      case 'iso_rated': return <ActiveBadge active={r.iso_rated} />;
      case 'primary_contact_name': return <span className="text-sm">{r.primary_contact_name || '—'}</span>;
      case 'primary_contact_phone': return <span className="text-sm font-mono text-zinc-600">{r.primary_contact_phone || '—'}</span>;
      case 'active_contracts': {
        const n = r.active_contracts || 0;
        return <span className={`text-sm font-mono tabular-nums ${n > 0 ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>{n}</span>;
      }
      case 'sic_description': return <span className="text-sm truncate block max-w-[200px]" title={r.sic_description}>{r.sic_description || '—'}</span>;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, [toggleFav]);

  return (
    <AppShell>
      <PageHeader title="Supplier" description={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" />Export</Button>
          {canWriteData && <Button size="sm" onClick={openCreate} data-testid="supplier-add-button"><Plus className="h-4 w-4 mr-1" />Add New</Button>}
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="suppliers-view-tabs">
        <button onClick={() => setViewTab('dashboard')} data-testid="suppliers-tab-dashboard"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'dashboard' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <LayoutDashboard className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="suppliers-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <User className="h-4 w-4 inline mr-1.5" /> Suppliers
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="suppliers-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'dashboard' ? (
        <SuppliersDashboard />
      ) : viewTab === 'reports' ? (
        <ModuleReports reports={SUPPLIERS_REPORTS} apiPrefix="/api/module-reports/data" module="Suppliers" />
      ) : (<>
      <DataTableToolbar searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search suppliers…" toolbarSlotId="supplier-table-toolbar" />

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? <EmptyState title="No suppliers" action={<Button size="sm" onClick={openCreate}>Add Supplier</Button>} /> : (
          <InteractiveTable
            tableId="supplier-listing"
            defaultCols={supplierDefaultCols}
            data={records}
            renderCell={renderSupplierCell}
            onRowClick={(row) => openEdit(row.supplier_code)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="supplier_code"
            toolbarPortalId="supplier-table-toolbar"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} /></div>
      </div>
      </>)}

      {/* ── Supplier Edit Modal ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-5xl h-[92vh] flex flex-col p-0 overflow-hidden">
          {/* Sticky header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
            <h2 className="font-semibold text-base" style={{ fontFamily: 'Space Grotesk' }}>
              {isEdit ? `Edit Supplier — ${form.supplier_name}` : 'Add Supplier'}
            </h2>
            <div className="flex items-center gap-2 mr-2">
              <span className="text-sm text-muted-foreground">Active</span>
              <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
            </div>
          </div>

          {/* Tabs — fixed layout, no jumping */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <Tabs defaultValue="details" className="flex flex-col flex-1 min-h-0"
              onValueChange={() => {
                // Reset scroll to top when switching tabs
                const el = document.getElementById('supplier-tab-body');
                if (el) el.scrollTop = 0;
              }}>
              {/* Tab bar — absolutely fixed, never moves */}
              <div className="px-6 pt-3 flex-shrink-0 bg-card z-10">
                <TabsList className="w-full">
                  <TabsTrigger value="details"    className="flex-1">Supplier Details</TabsTrigger>
                  <TabsTrigger value="financial"  className="flex-1">Financial</TabsTrigger>
                  <TabsTrigger value="contacts"   className="flex-1">
                    Contacts {(form.contacts||[]).length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1">{form.contacts.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="categories" className="flex-1">
                    Categories {(form.category_codes||[]).length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1">{form.category_codes.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="documents"  className="flex-1">
                    Documents {(form.documents||[]).length > 0 && <Badge className="ml-1 text-[10px] h-4 px-1">{form.documents.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="punchout"   className="flex-1">Punchout</TabsTrigger>
                  <TabsTrigger value="compliance" className="flex-1">Compliance</TabsTrigger>
                  <TabsTrigger value="co2e"       className="flex-1">CO2e</TabsTrigger>
                  <TabsTrigger value="change-history" className="flex-1">Change History</TabsTrigger>
                </TabsList>
              </div>

              {/* Scrollable tab body — fixed height container, content scrolls inside */}
              <div id="supplier-tab-body" className="flex-1 overflow-y-auto px-6 pb-4" style={{ minHeight: '400px' }}>

                {/* ── Supplier Details ── */}
                <TabsContent value="details" className="mt-4 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Supplier Code *</Label><Input value={form.supplier_code} onChange={e => f('supplier_code', e.target.value)} disabled={isEdit} /></div>
                    <div className="space-y-1.5"><Label>Supplier Name *</Label><Input value={form.supplier_name} onChange={e => f('supplier_name', e.target.value)} /></div>
                  </div>

                  {/* Address autocomplete */}
                  <div className="space-y-3 border rounded-xl p-4 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">Address</p>
                      <span className="text-xs text-muted-foreground">Start typing below to auto-fill · US &amp; UK supported</span>
                    </div>
                    <AddressLookup
                      placeholder="Type address to search and auto-fill fields below…"
                      onSelect={({ address, city, state_province, zip_postal, country }) => {
                        if (address) f('address', address);
                        if (city)    f('city', city);
                        if (state_province) f('state_province', state_province);
                        if (zip_postal)     f('zip_postal', zip_postal);
                        if (country)        f('country', country);
                      }}
                    />
                    <div className="grid grid-cols-2 gap-3 mt-1">
                      <div className="col-span-2 space-y-1"><Label className="text-xs">Street Address</Label><Input value={form.address} onChange={e => f('address', e.target.value)} placeholder="Street address" /></div>
                      <div className="space-y-1"><Label className="text-xs">City</Label><Input value={form.city} onChange={e => f('city', e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">State / Province</Label><Input value={form.state_province} onChange={e => f('state_province', e.target.value)} /></div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between"><Label className="text-xs">Zip / Postal Code</Label>
                          {form.country === 'United Kingdom' && (
                            <span className="text-[10px] text-primary font-medium">UK postcode lookup ↓</span>
                          )}
                        </div>
                        <Input value={form.zip_postal} onChange={e => f('zip_postal', e.target.value)} />
                        {form.country === 'United Kingdom' && (
                          <div className="mt-1">
                            <PostcodeLookup onFound={({ zip_postal, city, state_province }) => {
                              f('zip_postal', zip_postal);
                              if (city)           f('city', city);
                              if (state_province) f('state_province', state_province);
                            }} />
                          </div>
                        )}
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Country</Label>
                        <select value={form.country} onChange={e => f('country', e.target.value)}
                          className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                          {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Website</Label><Input value={form.website} onChange={e => f('website', e.target.value)} className="h-8 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs">DUNS Number</Label><Input value={form.duns_number} onChange={e => f('duns_number', e.target.value.replace(/\D/g, '').slice(0, 9))} className="h-8 text-sm" placeholder="9-digit" maxLength={9} /></div>
                    <div className="space-y-1 relative">
                      <Label className="text-xs">SIC Code</Label>
                      <Input value={sicSearchTerm || (form.sic_code ? `${form.sic_code} — ${form.sic_description || ''}` : '')}
                        onChange={e => searchSicCodes(e.target.value)}
                        onFocus={() => { if (sicResults.length) setSicDropOpen(true); }}
                        className="h-8 text-sm" placeholder="Search code or industry..." />
                      {sicDropOpen && sicResults.length > 0 && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl max-h-40 overflow-y-auto">
                          {sicResults.map(s => (
                            <button key={s.code} onClick={() => { f('sic_code', s.code); f('sic_description', s.description); setSicSearchTerm(`${s.code} — ${s.description}`); setSicDropOpen(false); }}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/30 flex items-center gap-2 border-b last:border-b-0">
                              <span className="font-semibold text-zinc-900 w-12">{s.code}</span>
                              <span>{s.description}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Registration Number</Label><Input value={form.registration_number} onChange={e => f('registration_number', e.target.value)} className="h-8 text-sm" /></div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    <div className="space-y-1"><Label className="text-xs">Type</Label>
                      <select value={form.supplier_type} onChange={e => f('supplier_type', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                        {SUPPLIER_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Status</Label>
                      <select value={form.status} onChange={e => f('status', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                        {SUPPLIER_STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end gap-4 pb-0.5">
                      {[['sub_contractor','Sub-Contractor'],['supplier_module','Supplier Portal Access']].map(([k,l]) => (
                        <label key={k} className="flex items-center gap-1.5 cursor-pointer"><Switch checked={form[k]} onCheckedChange={v => f(k,v)} id={`s-${k}`} /><span className="text-xs">{l}</span></label>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* ── Financial ── */}
                <TabsContent value="financial" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Sort / Swift / IBAN / ABA</Label>
                      <Input value={form.financial_details?.sort_swift_iban||''} onChange={e => fd('sort_swift_iban',e.target.value)}
                        placeholder="e.g. CHASUS33, 021000021, GB29NWBK..."
                        onBlur={async (e) => {
                          const code = e.target.value?.trim();
                          if (!code || code.length < 4) return;
                          try {
                            const r = await api.get('/api/system/bank-lookup', { params: { code } });
                            if (r.data.bank_name) {
                              fd('bank_name', r.data.bank_name);
                              toast.success(`Bank identified: ${r.data.bank_name}${r.data.city ? ` (${r.data.city})` : ''}`);
                            }
                          } catch {}
                        }} />
                    </div>
                    <div className="space-y-1.5"><Label>Account Number</Label><Input value={form.financial_details?.account_number||''} onChange={e => fd('account_number',e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Bank Name</Label><Input value={form.financial_details?.bank_name||''} onChange={e => fd('bank_name',e.target.value)} className="bg-muted/30" /></div>
                    <div className="space-y-1.5"><Label>Tax ID / VAT Number</Label><Input value={form.financial_details?.tax_id||''} onChange={e => fd('tax_id',e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Currency Code</Label>
                      <DataLookupField value={form.financial_details?.currency_code||'USD'} onChange={v => fd('currency_code',v)} apiEndpoint="/api/data/currencies" dataTestId="supplier-currency" />
                    </div>
                    <div className="space-y-1.5"><Label>Payment Terms</Label><Input value={form.financial_details?.payment_terms||''} onChange={e => fd('payment_terms',e.target.value)} /></div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.small_firm} onCheckedChange={v => f('small_firm',v)} /><span className="text-sm">Small Firm</span></label>
                    </div>
                    <div className="col-span-2 border rounded-lg p-3 bg-muted/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">Tax Rates</Label>
                        <button type="button" disabled={taxLooking} className="text-[10px] text-primary hover:underline disabled:opacity-50" onClick={async () => {
                          setTaxLooking(true);
                          try {
                            const r = await api.get('/api/system/tax-rate-lookup', { params: { zip_code: form.zip_postal || '', country: form.country || '', state: form.state_province || '' } });
                            if (r.data.source === 'unknown') {
                              toast.error('Could not determine tax rate for this address. Please enter manually.');
                            } else {
                              const t = r.data.tax_type;
                              if (t === 'us') {
                                fd('state_tax', fmtInput(r.data.state_tax));
                                fd('city_tax', fmtInput(r.data.city_tax));
                                fd('sales_tax', fmtInput(r.data.combined));
                              } else if (t === 'canada') {
                                fd('federal_tax', fmtInput(r.data.federal_tax));
                                fd('provincial_tax', fmtInput(r.data.provincial_tax));
                                fd('sales_tax', fmtInput(r.data.combined));
                              } else if (t === 'uk') {
                                fd('sales_tax', fmtInput(r.data.vat));
                              } else {
                                fd('sales_tax', fmtInput(r.data.combined));
                              }
                              toast.success(r.data.breakdown);
                            }
                          } catch { toast.error('Tax lookup failed'); }
                          setTaxLooking(false);
                        }}>{taxLooking ? 'Looking up...' : 'Lookup from address'}</button>
                      </div>
                      {(() => {
                        const ctry = (form.country || '').toLowerCase();
                        const isUS = ['united states', 'us', 'usa'].includes(ctry);
                        const isCA = ['canada', 'ca'].includes(ctry);
                        const isUK = ['united kingdom', 'uk', 'gb', 'great britain'].includes(ctry);
                        const combined = parseFloat(form.financial_details?.sales_tax || 0);

                        if (isUS) return (
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1"><Label className="text-xs">State Tax %</Label><Input type="number" step="0.001" value={form.financial_details?.state_tax||0} onChange={e => { fd('state_tax', e.target.value); fd('sales_tax', fmtInput(parseFloat(e.target.value||0) + parseFloat(form.financial_details?.city_tax||0))); }} /></div>
                            <div className="space-y-1"><Label className="text-xs">City / County Tax %</Label><Input type="number" step="0.001" value={form.financial_details?.city_tax||0} onChange={e => { fd('city_tax', e.target.value); fd('sales_tax', fmtInput(parseFloat(form.financial_details?.state_tax||0) + parseFloat(e.target.value||0))); }} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Combined Tax %</Label><Input type="number" step="0.001" value={form.financial_details?.sales_tax} readOnly className="bg-muted font-semibold" /></div>
                          </div>
                        );
                        if (isCA) return (
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1"><Label className="text-xs">Federal (GST) %</Label><Input type="number" step="0.001" value={form.financial_details?.federal_tax||5} onChange={e => { fd('federal_tax', e.target.value); fd('sales_tax', fmtInput(parseFloat(e.target.value||0) + parseFloat(form.financial_details?.provincial_tax||0))); }} /></div>
                            <div className="space-y-1"><Label className="text-xs">Provincial (PST/HST) %</Label><Input type="number" step="0.001" value={form.financial_details?.provincial_tax||0} onChange={e => { fd('provincial_tax', e.target.value); fd('sales_tax', fmtInput(parseFloat(form.financial_details?.federal_tax||0) + parseFloat(e.target.value||0))); }} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Combined Tax %</Label><Input type="number" step="0.001" value={form.financial_details?.sales_tax} readOnly className="bg-muted font-semibold" /></div>
                          </div>
                        );
                        if (isUK) return (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs">VAT %</Label><Input type="number" step="0.001" value={form.financial_details?.sales_tax} onChange={e => fd('sales_tax', e.target.value)} onBlur={() => fd('sales_tax', fmtInput(form.financial_details?.sales_tax))} /></div>
                          </div>
                        );
                        return (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs">Sales Tax %</Label><Input type="number" step="0.001" value={form.financial_details?.sales_tax} onChange={e => fd('sales_tax', e.target.value)} onBlur={() => fd('sales_tax', fmtInput(form.financial_details?.sales_tax))} /></div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="space-y-1.5"><Label>Default Discount %</Label><Input type="number" step="0.0001" value={form.financial_details?.discount} onChange={e => fd('discount',e.target.value)} onBlur={() => fd('discount',fmtInput(form.financial_details?.discount))} /></div>
                  </div>
                </TabsContent>

                {/* ── Contacts ── */}
                <TabsContent value="contacts" className="mt-4 space-y-4">
                  {/* Add new contact form */}
                  <div className="border rounded-xl p-4 bg-muted/10 space-y-3">
                    <p className="text-sm font-semibold">Add Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs flex items-center gap-1"><User className="h-3 w-3" /> Full Name *</Label>
                        <Input value={newContact.name} onChange={e => setNewContact(p=>({...p,name:e.target.value}))} placeholder="Jane Smith" className="h-8" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs flex items-center gap-1"><Briefcase className="h-3 w-3" /> Position / Title</Label>
                        <Input value={newContact.position||''} onChange={e => setNewContact(p=>({...p,position:e.target.value}))} placeholder="e.g. Account Manager" className="h-8" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email</Label>
                        <Input type="email" value={newContact.email} onChange={e => setNewContact(p=>({...p,email:e.target.value}))} placeholder="jane@supplier.com" className="h-8" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label>
                        <Input value={newContact.phone||''} onChange={e => setNewContact(p=>({...p,phone:e.target.value}))} placeholder="+1 555 000 0000" className="h-8" />
                      </div>
                    </div>
                    <Button size="sm" onClick={addContact} disabled={!newContact.name}>
                      <Plus className="h-4 w-4 mr-1" /> Add Contact
                    </Button>
                  </div>

                  {/* Contact list */}
                  {(form.contacts||[]).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No contacts yet. Add one above.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Click the ★ to set the main contact</p>
                      {form.contacts.map((c, i) => (
                        <div key={i} className={`p-3 rounded-xl border transition-colors ${c.is_main ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
                          {editingContactIdx === i ? (
                            <ContactEditForm contact={c} onSave={(updated) => updateContact(i, updated)} onCancel={() => setEditingContactIdx(null)} />
                          ) : (
                            <div className="flex items-start gap-3 cursor-pointer" onClick={() => setEditingContactIdx(i)}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setMainContact(i); }}
                                title={c.is_main ? 'Main contact' : 'Set as main contact'}
                                className={`mt-0.5 flex-shrink-0 transition-colors ${c.is_main ? 'text-amber-400' : 'text-muted-foreground/40 hover:text-amber-400'}`}
                                aria-label={c.is_main ? 'Main contact' : 'Set as main contact'}
                                aria-pressed={c.is_main}
                              >
                                <Star className={`h-4 w-4 ${c.is_main ? 'fill-amber-400' : ''}`} />
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-sm">{c.name}</span>
                                  {c.is_main && <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20 border">Main Contact</Badge>}
                                </div>
                                {c.position && <p className="text-xs text-muted-foreground mt-0.5">{c.position}</p>}
                                <div className="flex flex-wrap gap-4 mt-1 text-xs text-muted-foreground">
                                  {c.email && <span className="flex items-center gap-1">✉ {c.email}</span>}
                                  {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                                </div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); removeContact(i); }} className="text-destructive hover:opacity-70 flex-shrink-0" aria-label="Remove contact">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── Category Details ── */}
                <TabsContent value="categories" className="mt-4">
                  <SupplierCategoryAdder form={form} setForm={setForm} />
                  {(form.category_codes||[]).length===0 ? <p className="text-sm text-muted-foreground text-center py-6">No categories linked.</p> : (
                    <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Description</TableHead><TableHead></TableHead></TableRow></TableHeader>
                      <TableBody>{form.category_codes.map((c,i)=>(
                        <TableRow key={i}><TableCell className="font-mono text-xs">{c.code}</TableCell><TableCell>{c.description}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" onClick={()=>setForm(p=>({...p,category_codes:p.category_codes.filter((_,j)=>j!==i)}))}><Trash2 className="h-3.5 w-3.5 text-destructive"/></Button></TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </TabsContent>

                {/* ── Attach Documents ── */}
                <TabsContent value="documents" className="mt-4">
                  <SupplierDocuments form={form} setForm={setForm} isEdit={isEdit} />
                </TabsContent>

                {/* ── Punchout ── */}
                <TabsContent value="punchout" className="mt-4 space-y-4">
                  <label className="flex items-center gap-3"><Switch checked={form.punchout_details?.enabled} onCheckedChange={v=>fp('enabled',v)} /><span className="font-semibold text-sm">Punchout Supplier</span></label>
                  {form.punchout_details?.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5"><Label>Protocol</Label><select value={form.punchout_details?.protocol||'cXML'} onChange={e=>fp('protocol',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>cXML</option><option>OCI</option></select></div>
                      <div className="space-y-1.5"><Label>Type</Label><select value={form.punchout_details?.punchout_type||'Test'} onChange={e=>fp('punchout_type',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>Test</option><option>Production</option></select></div>
                      <div className="space-y-1.5"><Label>Buyer Identity *</Label><Input value={form.punchout_details?.buyer_identity||''} onChange={e=>fp('buyer_identity',e.target.value)} /></div>
                      <div className="space-y-1.5"><Label>Supplier Identity *</Label><Input value={form.punchout_details?.supplier_identity||''} onChange={e=>fp('supplier_identity',e.target.value)} /></div>
                      <div className="space-y-1.5"><Label>Shared Secret *</Label><Input type="password" value={form.punchout_details?.shared_secret||''} onChange={e=>fp('shared_secret',e.target.value)} /></div>
                      <div className="space-y-1.5"><Label>Catalog URL *</Label><Input value={form.punchout_details?.catalog_url||''} onChange={e=>fp('catalog_url',e.target.value)} /></div>
                      <div className="col-span-2 space-y-1.5"><Label>Order URL</Label><Input value={form.punchout_details?.order_url||''} onChange={e=>fp('order_url',e.target.value)} /></div>
                    </div>
                  )}
                </TabsContent>

                {/* ── Compliance ── */}
                <TabsContent value="compliance" className="mt-4">
                  <div className="grid grid-cols-[1fr_1fr] gap-4 mb-4">
                    {/* Left: Certifications (CRUD, scrollable) */}
                    <div className="border rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm text-primary">Formal Certifications</h3>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                          const certs = [...(form.compliance?.certifications || [])];
                          certs.push({ id: Date.now().toString(), name: '', start_date: '', expiry_date: '', verified: false });
                          setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                        }}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                      </div>
                      <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                        {(form.compliance?.certifications || []).length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-4">No certifications recorded. Click "Add" to start.</p>
                        )}
                        {(form.compliance?.certifications || []).map((cert, idx) => (
                          <div key={cert.id || idx} className="border rounded-lg p-3 bg-muted/20 space-y-2">
                            <div className="flex items-start gap-2">
                              <div className="flex-1 space-y-1">
                                <Input value={cert.name} onChange={e => {
                                  const certs = [...(form.compliance?.certifications || [])];
                                  certs[idx] = { ...certs[idx], name: e.target.value };
                                  setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                                }} className="h-8 text-sm" placeholder="e.g. SOC 2 Type II, ISO 27001, NIST 800-172" />
                              </div>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => {
                                const certs = (form.compliance?.certifications || []).filter((_, i) => i !== idx);
                                setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                              }}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-muted-foreground">Start Date</Label>
                                <Input type="date" value={cert.start_date || ''} onChange={e => {
                                  const certs = [...(form.compliance?.certifications || [])];
                                  const newStart = e.target.value;
                                  certs[idx] = { ...certs[idx], start_date: newStart };
                                  // Auto-default expiry to start + 12 months if not already set or if expiry matches previous auto-default
                                  if (newStart && (!certs[idx].expiry_date || certs[idx]._auto_expiry)) {
                                    const d = new Date(newStart);
                                    d.setFullYear(d.getFullYear() + 1);
                                    certs[idx].expiry_date = d.toISOString().split('T')[0];
                                    certs[idx]._auto_expiry = true;
                                  }
                                  setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                                }} className="h-7 text-xs" />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px] text-muted-foreground">Expiry Date</Label>
                                <Input type="date" value={cert.expiry_date || ''} onChange={e => {
                                  const certs = [...(form.compliance?.certifications || [])];
                                  certs[idx] = { ...certs[idx], expiry_date: e.target.value, _auto_expiry: false };
                                  setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                                }} className="h-7 text-xs" />
                              </div>
                              <div className="flex items-end pb-0.5">
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                  <input type="checkbox" checked={cert.verified || false} onChange={e => {
                                    const certs = [...(form.compliance?.certifications || [])];
                                    certs[idx] = { ...certs[idx], verified: e.target.checked };
                                    setForm(p => ({ ...p, compliance: { ...p.compliance, certifications: certs } }));
                                  }} className="h-3.5 w-3.5 rounded" />
                                  <span className={cert.verified ? 'text-green-700 font-semibold' : 'text-muted-foreground'}>Verified by Procurement</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right: Supplier Diversity & Organization (moved here) */}
                    <div className="space-y-4">
                      <div className="border rounded-xl p-4">
                        <h3 className="font-semibold text-sm text-primary mb-3">Supplier Diversity</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {[['mbe','Minority Business Enterprise (MBE)'],['wbe','Women Business Enterprise (WBE)'],['vosb','Veteran-Owned Small Business (VOSB)'],['lgbte','LGBTE Owned Business Enterprise'],['hubzone','HUBZone'],['sbe','Small Business Enterprise (SBE)'],['sdv','Service Disabled Veteran'],['sdvosb','SDVOSB'],['vbe','Veteran Business Enterprise (VBE)'],['sdb','Small Disadvantaged Business'],['dobe','Disability-Owned Business (DOBE)']].map(([k,l])=>(
                            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                              <input type="checkbox" checked={form.compliance?.diversity?.[k]||false} onChange={e=>fc('diversity',k,e.target.checked)} className="h-3.5 w-3.5 rounded border-input"/>{l}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="border rounded-xl p-4">
                        <h3 className="font-semibold text-sm text-primary mb-3">Organization</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1"><Label className="text-xs">{t('term.organisationSize')}</Label><select value={form.compliance?.organization?.size||''} onChange={e=>fc('organization','size',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-xs"><option value="">--</option><option>{'< 100'}</option><option>100-500</option><option>500-1000</option><option>{'> 1000'}</option></select></div>
                          <div className="space-y-1"><Label className="text-xs">Conflicts of Interest</Label><select value={form.compliance?.organization?.conflicts||'None'} onChange={e=>fc('organization','conflicts',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-xs"><option>None</option><option>Low</option><option>Medium</option><option>High</option></select></div>
                          <div className="space-y-1"><Label className="text-xs">Active Contracts</Label><select value={form.compliance?.organization?.active_contracts||'No'} onChange={e=>fc('organization','active_contracts',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-xs"><option>Yes</option><option>No</option></select></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Risk & Compliance tabs below */}
                  <Tabs defaultValue="risk-info">
                    <TabsList className="mb-4">
                      <TabsTrigger value="risk-info">Risk And Compliance Information</TabsTrigger>
                      <TabsTrigger value="risk-status">Risk And Compliance Status</TabsTrigger>
                      <TabsTrigger value="analysis">Compliance Analysis</TabsTrigger>
                    </TabsList>

                    {/* Risk And Compliance Information - matches Eyvo screenshot */}
                    <TabsContent value="risk-info" className="mt-1">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        {/* Left Column */}
                        <div className="space-y-4">
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">UK Corporate criminal offenses (CCO) risk</Label>
                            <select value={form.compliance?.risk_info?.cco_risk||'Low'} onChange={e=>fc('risk_info','cco_risk',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>Low</option><option>Medium</option><option>High</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">UK Modern Slavery risk</Label>
                            <select value={form.compliance?.risk_info?.modern_slavery||'Low'} onChange={e=>fc('risk_info','modern_slavery',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>Low</option><option>Medium</option><option>High</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Business Continuity Plan Risk</Label>
                            <select value={form.compliance?.risk_info?.bcp_risk||'No'} onChange={e=>fc('risk_info','bcp_risk',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>No</option><option>Yes</option><option>Low</option><option>Medium</option><option>High</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Access to Premises</Label>
                            <div className="flex items-center gap-3">
                              <select value={form.compliance?.risk_info?.access_premises||'Occasional'} onChange={e=>fc('risk_info','access_premises',e.target.value)} className="h-9 w-40 rounded-lg border border-input bg-background px-3 text-sm"><option>None</option><option>Occasional</option><option>Frequent</option><option>Permanent</option></select>
                              <label className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" name="escort" value="Escorted" checked={form.compliance?.risk_info?.escort_type==='Escorted'} onChange={e=>fc('risk_info','escort_type',e.target.value)} className="h-4 w-4" /> Escorted</label>
                              <label className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" name="escort" value="Unescorted" checked={form.compliance?.risk_info?.escort_type!=='Escorted'} onChange={e=>fc('risk_info','escort_type',e.target.value)} className="h-4 w-4" /> Unescorted</label>
                            </div></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Access to Client Data</Label>
                            <select value={form.compliance?.risk_info?.client_data||'Yes'} onChange={e=>fc('risk_info','client_data',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>Yes</option><option>No</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Access to Personal Identifiable Information (PII)</Label>
                            <select value={form.compliance?.risk_info?.pii_access||'No'} onChange={e=>fc('risk_info','pii_access',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>No</option><option>Yes</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Access to Highly Sensitive Information (HSI)</Label>
                            <select value={form.compliance?.risk_info?.hsi_access||'No'} onChange={e=>fc('risk_info','hsi_access',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>No</option><option>Yes</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Privacy Designation</Label>
                            <select value={form.compliance?.risk_info?.privacy_designation||'Controller'} onChange={e=>fc('risk_info','privacy_designation',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>Controller</option><option>Processor</option><option>Joint Controller</option><option>Sub-Processor</option><option>N/A</option></select></div>
                        </div>
                        {/* Right Column */}
                        <div className="space-y-4">
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Privacy Jurisdiction</Label>
                            <select value={form.compliance?.risk_info?.privacy_jurisdiction||'UK'} onChange={e=>fc('risk_info','privacy_jurisdiction',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>UK</option><option>EU</option><option>US</option><option>Other</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Date transfers out of Jurisdiction</Label>
                            <select value={form.compliance?.risk_info?.data_transfers_out||'No'} onChange={e=>fc('risk_info','data_transfers_out',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>No</option><option>Yes</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Subcontractor or 3rd parties</Label>
                            <div className="flex items-center gap-2">
                              <select value={form.compliance?.risk_info?.subcontractor||'No'} onChange={e=>fc('risk_info','subcontractor',e.target.value)} className="h-9 w-40 rounded-lg border border-input bg-background px-3 text-sm"><option>No</option><option>Yes</option></select>
                              <Input value={form.compliance?.risk_info?.subcontractor_name||''} onChange={e=>fc('risk_info','subcontractor_name',e.target.value)} placeholder="Subcontractor name" className="text-sm" />
                            </div></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Data Description</Label>
                            <Textarea value={form.compliance?.risk_info?.data_description||''} onChange={e=>{if(e.target.value.length<=500)fc('risk_info','data_description',e.target.value)}} rows={4} className="text-sm" />
                            <p className="text-xs text-muted-foreground text-right">characters remaining {500-(form.compliance?.risk_info?.data_description?.length||0)}</p></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Conduct and Ethics</Label>
                            <select multiple value={form.compliance?.risk_info?.conduct_ethics||[]} onChange={e=>fc('risk_info','conduct_ethics',Array.from(e.target.selectedOptions,o=>o.value))}
                              className="h-40 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                              {['SOX','IT Certifications','CCPA','Green Policy','Child Labor','Anti-Money laundering','Supplier Diversity','GDPR','Bribery Act'].map(o=><option key={o} value={o}>{o}</option>)}
                            </select>
                            <p className="text-xs text-muted-foreground">Note: To select multiple options press Ctrl key and select.</p></div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Risk And Compliance Status - matches Eyvo screenshot */}
                    <TabsContent value="risk-status" className="mt-1">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        {/* Left Column - Certifications */}
                        <div className="space-y-4">
                          {[
                            {k:'procurement_certified',l:'Procurement Certified',opts:['NA','Yes','No','Pending']},
                            {k:'security_certified',l:'Security Certified',opts:['NA','Yes','No','Pending']},
                            {k:'conflicts_certified',l:'Conflicts Certified',opts:['NA','Yes','No','Pending']},
                            {k:'counsel_certified',l:'Counsel Certified',opts:['No','Yes','NA','Pending']},
                          ].map(item=>(
                            <div key={item.k} className="space-y-1.5">
                              <Label className="text-xs font-semibold text-zinc-900">{item.l}</Label>
                              <div className="flex items-center gap-2">
                                <select value={form.compliance?.risk_status?.[item.k]||item.opts[0]} onChange={e=>fc('risk_status',item.k,e.target.value)}
                                  className="h-9 w-32 rounded-lg border border-input bg-background px-3 text-sm">
                                  {item.opts.map(o=><option key={o} value={o}>{o}</option>)}
                                </select>
                                <Input type="date" value={form.compliance?.risk_status?.[`${item.k}_date`]||''} onChange={e=>fc('risk_status',`${item.k}_date`,e.target.value)}
                                  className="text-sm w-40" placeholder="Next certification date" />
                                <Button variant="ghost" size="sm" onClick={()=>fc('risk_status',`${item.k}_date`,'')}><X className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          ))}
                          <div className="space-y-1.5 pt-2"><Label className="text-xs">Other Certifications</Label>
                            <Textarea value={form.compliance?.risk_status?.other_certifications||''} onChange={e=>{if(e.target.value.length<=250)fc('risk_status','other_certifications',e.target.value)}} rows={3} className="text-sm" placeholder="Other Certifications" />
                            <p className="text-xs text-muted-foreground text-right">characters remaining {250-(form.compliance?.risk_status?.other_certifications?.length||0)}</p></div>
                          <div className="space-y-1.5"><Label className="text-xs">Supplier compliance notification sent/response/follow ups (potential)</Label>
                            <Textarea value={form.compliance?.risk_status?.compliance_notes||''} onChange={e=>{if(e.target.value.length<=250)fc('risk_status','compliance_notes',e.target.value)}} rows={3} className="text-sm" placeholder="Supplier compliance notification sent/response/follow ups (potential)" />
                            <p className="text-xs text-muted-foreground text-right">characters remaining {250-(form.compliance?.risk_status?.compliance_notes?.length||0)}</p></div>
                        </div>
                        {/* Right Column */}
                        <div className="space-y-4">
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Certification of Insurance Provided</Label>
                            <select value={form.compliance?.risk_status?.insurance_provided||'NA'} onChange={e=>fc('risk_status','insurance_provided',e.target.value)}
                              className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"><option>NA</option><option>Yes</option><option>No</option><option>Pending</option></select></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold text-zinc-900">Contracts in Place</Label>
                            <select multiple value={form.compliance?.risk_status?.contracts_in_place||[]} onChange={e=>fc('risk_status','contracts_in_place',Array.from(e.target.selectedOptions,o=>o.value))}
                              className="h-32 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                              {['MSA','NDA','DPA','SOW','Other'].map(o=><option key={o} value={o}>{o}</option>)}
                            </select>
                            <p className="text-xs text-muted-foreground">Note: To select multiple options press Ctrl key and select.</p></div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Compliance Analysis */}
                    <TabsContent value="analysis" className="mt-1">
                      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground border rounded-md">
                        Compliance Analysis will be calculated based on the risk and compliance data entered above.
                      </div>
                    </TabsContent>
                  </Tabs>
                </TabsContent>

                {/* ── CO2e Emissions ── */}
                <TabsContent value="co2e" className="mt-4">
                  <SupplierEmissionsTab form={form} setForm={setForm} isEdit={isEdit} />
                </TabsContent>

                {/* ── Change History ── */}
                <TabsContent value="change-history" className="mt-4">
                  <SupplierChangeHistory supplierCode={form.supplier_code} />
                </TabsContent>


              </div>{/* end scrollable body */}
            </Tabs>
          </div>

          {/* Sticky footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t bg-card flex-shrink-0">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && canWriteData && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            {canWriteData && <Button onClick={save} disabled={saving || !form.supplier_code || !form.supplier_name}>
              {saving ? 'Saving...' : 'Save'}
            </Button>}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


function ContactEditForm({ contact, onSave, onCancel }) {
  const [edit, setEdit] = useState({ ...contact });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Name</Label><Input value={edit.name||''} onChange={e => setEdit(p=>({...p,name:e.target.value}))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs">Position</Label><Input value={edit.position||''} onChange={e => setEdit(p=>({...p,position:e.target.value}))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" value={edit.email||''} onChange={e => setEdit(p=>({...p,email:e.target.value}))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs">Phone</Label><Input value={edit.phone||''} onChange={e => setEdit(p=>({...p,phone:e.target.value}))} className="h-8 text-sm" /></div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}><X className="h-3 w-3 mr-1" /> Cancel</Button>
        <Button size="sm" onClick={() => onSave(edit)} disabled={!edit.name}><Save className="h-3 w-3 mr-1" /> Save</Button>
      </div>
    </div>
  );
}


// ── Supplier Emissions Tab ─────────────────────────────────────────────────
function SupplierEmissionsTab({ form, setForm, isEdit }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [spendEmissions, setSpendEmissions] = React.useState(null);
  const [aiLooking, setAiLooking] = React.useState(false);

  const esg = form.emissions || {};
  const setEsg = (field, val) => setForm(p => ({ ...p, emissions: { ...p.emissions, [field]: val } }));

  // Load spend-based emissions from dedicated endpoint
  React.useEffect(() => {
    if (isEdit && form.supplier_code) {
      api.get(`/api/system/supplier-spend-emissions/${form.supplier_code}`).then(r => {
        setSpendEmissions(r.data);
      }).catch(() => { setSpendEmissions({ total_spend: 0, total_co2: 0, order_count: 0, co2_per_dollar: 0 }); });
    }
  }, [isEdit, form.supplier_code]); // eslint-disable-line

  const aiLookup = async () => {
    if (!form.supplier_name) { toast.error('Supplier name required'); return; }
    setAiLooking(true);
    try {
      const r = await api.post('/api/system/ai-emissions-lookup', { company_name: form.supplier_name, country: form.country || '' });
      if (r.data.found) {
        const aiData = r.data.data;
        const sourceNote = `AI Lookup (${fmtDate(new Date(), tz)}): Data retrieved for ${form.supplier_name} from ${r.data.source || 'public disclosures'}. ${r.data.notes || ''} This data is AI-estimated and requires independent verification.`;
        const existingNotes = esg.notes ? esg.notes + '\n\n' : '';
        setForm(p => ({ ...p, emissions: { ...p.emissions, ...aiData, source: 'AI Lookup', confidence: 'Low', ai_retrieved: true, retrieved_at: new Date().toISOString(), notes: existingNotes + sourceNote } }));
        toast.success('Emissions data retrieved');
      } else {
        toast.info('No published emissions data found for this supplier');
      }
    } catch (e) { toast.error(getApiErrorMessage(e, 'Lookup failed')); }
    setAiLooking(false);
  };

  const fmtN = (v) => v != null && v !== '' ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';

  return (
    <div className="space-y-4">
      {/* Section A: Supplier ESG / Published Emissions */}
      <div className="border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-sm text-primary">Supplier Published Emissions (ESG Data)</h3>
            <p className="text-[10px] text-muted-foreground">Reported emissions from sustainability reports, CDP disclosures, or self-reported data</p>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={aiLookup} disabled={aiLooking}>
            {aiLooking ? 'Searching...' : 'AI Lookup Published Data'}
          </Button>
        </div>
        {esg.ai_retrieved && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
            AI-estimated — requires verification. Source: {esg.source || 'AI'}. Retrieved: {esg.retrieved_at ? fmtDate(esg.retrieved_at, tz) : '—'}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Scope 1 Emissions (tCO₂e/yr)</Label>
            <p className="text-[10px] text-muted-foreground">Direct emissions from owned/controlled sources</p>
            <Input type="number" step="0.01" value={esg.scope1 ?? ''} onChange={e => setEsg('scope1', parseFloat(e.target.value) || '')} className="h-8 text-sm" placeholder="tonnes CO₂e" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Scope 2 Emissions (tCO₂e/yr)</Label>
            <p className="text-[10px] text-muted-foreground">Indirect from purchased energy</p>
            <Input type="number" step="0.01" value={esg.scope2 ?? ''} onChange={e => setEsg('scope2', parseFloat(e.target.value) || '')} className="h-8 text-sm" placeholder="tonnes CO₂e" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Scope 3 Emissions (tCO₂e/yr)</Label>
            <p className="text-[10px] text-muted-foreground">All other indirect (supply chain, logistics, etc.)</p>
            <Input type="number" step="0.01" value={esg.scope3 ?? ''} onChange={e => setEsg('scope3', parseFloat(e.target.value) || '')} className="h-8 text-sm" placeholder="tonnes CO₂e" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 mt-3">
          <div className="space-y-1">
            <Label className="text-xs">Carbon Intensity (kgCO₂e/$)</Label>
            <Input type="number" step="0.001" value={esg.carbon_intensity ?? ''} onChange={e => setEsg('carbon_intensity', parseFloat(e.target.value) || '')} className="h-8 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reporting Year</Label>
            <Input type="number" value={esg.reporting_year ?? ''} onChange={e => setEsg('reporting_year', e.target.value)} className="h-8 text-sm" placeholder="e.g. 2024" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data Source</Label>
            <select value={esg.source || ''} onChange={e => setEsg('source', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
              <option value="">Not specified</option>
              <option value="CDP">CDP Disclosure</option>
              <option value="sustainability_report">Sustainability Report</option>
              <option value="self_reported">Self-Reported</option>
              <option value="ecovadis">EcoVadis</option>
              <option value="estimated">Estimated (Industry Average)</option>
              <option value="AI Lookup">AI Lookup</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Confidence</Label>
            <select value={esg.confidence || ''} onChange={e => setEsg('confidence', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
              <option value="">Not rated</option>
              <option value="High">High (audited/verified)</option>
              <option value="Medium">Medium (self-reported)</option>
              <option value="Low">Low (estimated/AI)</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={esg.low_carbon_supplier || false} onChange={e => setEsg('low_carbon_supplier', e.target.checked)} className="h-3.5 w-3.5 rounded" />
            <span className={esg.low_carbon_supplier ? 'text-green-700 font-semibold' : ''}>Low Carbon Supplier</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={esg.net_zero_committed || false} onChange={e => setEsg('net_zero_committed', e.target.checked)} className="h-3.5 w-3.5 rounded" />
            <span className={esg.net_zero_committed ? 'text-green-700 font-semibold' : ''}>Net Zero Committed</span>
          </label>
        </div>
      </div>

      {/* Section B: Spend-Based Emissions (derived from your PO data) */}
      <div className="border rounded-xl p-4">
        <h3 className="font-semibold text-sm text-primary mb-1">Spend-Based Emissions (Your Orders)</h3>
        <p className="text-[10px] text-muted-foreground mb-3">Emissions attributable to this supplier based on your purchase order spend. Calculated from transaction-level product category emission factors.</p>
        {!spendEmissions ? (
          <p className="text-xs text-muted-foreground">{isEdit ? 'Calculating...' : 'Save the supplier first to see spend emissions.'}</p>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            <div className="border rounded-lg p-3 bg-blue-50">
              <p className="text-[10px] text-blue-600 font-semibold">Total Spend</p>
              <p className="text-lg font-bold text-blue-700">${fmtN(spendEmissions.total_spend)}</p>
              <p className="text-[10px] text-blue-500">{spendEmissions.order_count} order(s)</p>
            </div>
            <div className="border rounded-lg p-3 bg-green-50">
              <p className="text-[10px] text-green-600 font-semibold">Total CO₂e from Orders</p>
              <p className="text-lg font-bold text-green-700">{fmtN(spendEmissions.total_co2)} kg</p>
              <p className="text-[10px] text-green-500">Scope 3 Cat.1</p>
            </div>
            <div className="border rounded-lg p-3 bg-purple-50">
              <p className="text-[10px] text-purple-600 font-semibold">CO₂e per $ Spend</p>
              <p className="text-lg font-bold text-purple-700">{fmtN(spendEmissions.co2_per_dollar)} kg/$</p>
              <p className="text-[10px] text-purple-500">Your carbon intensity</p>
            </div>
            <div className="border rounded-lg p-3 bg-amber-50">
              <p className="text-[10px] text-amber-600 font-semibold">Annual Equivalent</p>
              <p className="text-lg font-bold text-amber-700">{fmtN(spendEmissions.total_co2 / 1000)} tCO₂e</p>
              <p className="text-[10px] text-amber-500">Metric tonnes</p>
            </div>
          </div>
        )}
      </div>

      {/* Section C: Notes */}
      <div className="border rounded-xl p-4">
        <h3 className="font-semibold text-sm text-primary mb-2">Emissions Notes</h3>
        <Textarea value={esg.notes || ''} onChange={e => setEsg('notes', e.target.value)} rows={2} className="text-sm" placeholder="Any notes about this supplier's emissions data, reduction targets, or sustainability initiatives..." />
      </div>
    </div>
  );
}


function SupplierChangeHistory({ supplierCode }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supplierCode) { setLoading(false); return; }
    api.get(`/api/items-suppliers/suppliers/${supplierCode}/change-history`)
      .then(r => setHistory(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [supplierCode]);

  if (loading) return <p className="text-xs text-muted-foreground text-center py-4">Loading...</p>;
  if (history.length === 0) return <p className="text-xs text-muted-foreground text-center py-6">No change history for this supplier.</p>;

  const sourceLabels = { internal: 'Internal', supplier_portal: 'Supplier Portal' };
  const sourceColors = { internal: 'bg-blue-100 text-blue-700', supplier_portal: 'bg-violet-100 text-violet-700' };

  return (
    <div className="space-y-3">
      {history.map((entry, idx) => (
        <div key={idx} className="border rounded-lg p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${sourceColors[entry.source] || 'bg-slate-100'}`}>{sourceLabels[entry.source] || entry.source}</span>
              <span className="text-xs font-medium">{entry.changed_by}</span>
            </div>
            <span className="text-xs text-slate-400">{entry.changed_at ? fmtDateTime(entry.changed_at, tz) : ''}</span>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="border-b">
              <th className="text-left py-1 font-semibold w-1/4">Field</th>
              <th className="text-left py-1 font-semibold w-[37.5%]">Before</th>
              <th className="text-left py-1 font-semibold w-[37.5%]">After</th>
            </tr></thead>
            <tbody>
              {(entry.changes || []).map((c, ci) => (
                <tr key={ci} className="border-b last:border-0">
                  <td className="py-1 font-medium text-slate-700">{c.field.replace(/_/g, ' ')}</td>
                  <td className="py-1 text-red-600 max-w-[200px] truncate" title={c.old_value}>{c.old_value || '—'}</td>
                  <td className="py-1 text-emerald-700 max-w-[200px] truncate" title={c.new_value}>{c.new_value || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
